import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SmartSchedulerComponent.css';
import notificationSound from '../../assets/sounds/notification-sound.mp3';

const SmartSchedulerComponent = () => {
  const [schedule, setSchedule] = useState(() => {
    const savedSchedule = localStorage.getItem('schedule');
    return savedSchedule ? JSON.parse(savedSchedule) : [
      { time: '06:00 AM', task: 'Wake up', id: 1 },
      { time: '07:00 AM', task: 'Breakfast', id: 2 }
    ];
  });
  const [newTask, setNewTask] = useState('');
  const [newTime, setNewTime] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [isAlarmRinging, setIsAlarmRinging] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('schedule', JSON.stringify(schedule));
  }, [schedule]);

  const handleNotification = useCallback((task) => {
    const now = new Date();
    const taskTime = parseTime(task.time);

    if (
      now.getHours() === taskTime.getHours() &&
      now.getMinutes() === taskTime.getMinutes() &&
      now.getSeconds() === 0
    ) {
      // Show browser notification
      if (Notification.permission === "granted") {
        new Notification(`Task Reminder`, { body: `It's ${task.time}. Time to ${task.task}` });
      }

      // Speak the task aloud
      const utterance = new SpeechSynthesisUtterance(`It's ${task.time}. Time to ${task.task}`);
      window.speechSynthesis.speak(utterance);

      // Play alarm sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0; // Reset to the start of the audio file
        audioRef.current.play().catch(error => console.error('Audio playback failed:', error));
      }
      setIsAlarmRinging(true);
    }
  }, []);

  useEffect(() => {
    const checkTasks = () => {
      schedule.forEach(task => handleNotification(task));
    };

    const intervalId = setInterval(checkTasks, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [schedule, handleNotification]);

  const addTask = () => {
    if (newTask && newTime) {
      const newId = schedule.length ? schedule[schedule.length - 1].id + 1 : 1;
      setSchedule([...schedule, { time: formatTime(newTime), task: newTask, id: newId }]);
      setNewTask('');
      setNewTime('');
    }
  };

  const deleteTask = (id) => {
    setSchedule(schedule.filter(task => task.id !== id));
  };

  const editTask = (id) => {
    const taskToEdit = schedule.find(task => task.id === id);
    if (taskToEdit) {
      setNewTask(taskToEdit.task);
      setNewTime(convertTimeTo24HourFormat(taskToEdit.time));
      setIsEditing(true);
      setEditingTaskId(id);
    }
  };

  const updateTask = () => {
    setSchedule(schedule.map(task => task.id === editingTaskId ? { ...task, time: formatTime(newTime), task: newTask } : task));
    setNewTask('');
    setNewTime('');
    setIsEditing(false);
    setEditingTaskId(null);
  };

  const stopAlarm = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsAlarmRinging(false);
  };

  const formatTime = (time) => {
    let [hours, minutes] = time.split(':');
    let period = 'AM';
    hours = parseInt(hours);
    if (hours >= 12) {
      period = 'PM';
      if (hours > 12) {
        hours -= 12;
      }
    } else if (hours === 0) {
      hours = 12;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes} ${period}`;
  };

  const convertTimeTo24HourFormat = (time) => {
    const [hourMinute, period] = time.split(' ');
    let [hours, minutes] = hourMinute.split(':');
    hours = parseInt(hours);
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  const parseTime = (time) => {
    const [hourMinute, period] = time.split(' ');
    const [hours, minutes] = hourMinute.split(':');
    let hour = parseInt(hours);
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    const date = new Date();
    date.setHours(hour, parseInt(minutes), 0, 0);
    return date;
  };

  return (
    <div className="scheduler-container">
      <h2>Smart Scheduler</h2>
      <div className="task-list">
        {schedule.map(task => (
          <div key={task.id} className="task-item">
            <div>
              <span className="task-time">{task.time}</span> - <span className="task-text">{task.task}</span>
            </div>
            <div className="task-actions">
              <button onClick={() => editTask(task.id)}>Edit</button>
              <button onClick={() => deleteTask(task.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      <div className="add-task">
        <input
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          placeholder="HH:MM"
        />
        <input
          type="text"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder="Task"
        />
        <button onClick={isEditing ? updateTask : addTask}>
          {isEditing ? 'Update Task' : 'Add Task'}
        </button>
      </div>

      {isAlarmRinging && (
        <div className="alarm-notification">
          <div className="alarm-notification-content">
            <p>An alarm is ringing for a task!</p>
            <button className="dismiss-button" onClick={stopAlarm}>Dismiss</button>
          </div>
        </div>
      )}

      <audio ref={audioRef}>
        <source src={notificationSound} type="audio/mp3" />
      </audio>
    </div>
  );
};

export default SmartSchedulerComponent;
